import { useEffect, useState } from "react";
import { Download, Share, X, MonitorDown } from "lucide-react";
import { useI18n } from "@/i18n";

const KEY = "rh_install_hidden";      // quando riproporlo: data in millisecondi
const RINVIO = 7 * 24 * 60 * 60 * 1000;
const EVENTO = "rh-apri-installa";

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
// Dentro Instagram, Facebook, TikTok o Threads non esiste «Aggiungi a Home»: l'invito è solo un
// pannello che copre i pulsanti, quindi lì non si mostra.
const inApp = () => /instagram|fb[ia]v|fban|tiktok|threads|line\/|snapchat/i.test(window.navigator.userAgent);
const rinviato = () => Number(localStorage.getItem(KEY) || 0) > Date.now();

/** Si chiede da una voce di menu: «Installa l'app». */
export const apriInstalla = () => window.dispatchEvent(new Event(EVENTO));
export const installabile = () => !isStandalone() && !inApp();

/** Invito ad aggiungere il gestionale alla schermata Home (o al desktop): su Android e PC usa il
 *  prompt del browser, su iPhone spiega i due tocchi (Safari non espone alcun evento). */
export const InstallPrompt = () => {
  const { t } = useI18n();
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [chiesto, setChiesto] = useState(false);   // aperto a mano dal menu: ignora il rinvio

  useEffect(() => {
    if (isStandalone() || inApp()) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setPrompt(e);
      if (!rinviato()) setShow(true);
    };
    const aMano = () => {
      setChiesto(true);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener(EVENTO, aMano);
    // iPhone non emette l'evento: mostriamo le istruzioni dopo qualche secondo d'uso
    const id = isIOS() && !rinviato() ? setTimeout(() => setShow(true), 8000) : null;
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener(EVENTO, aMano);
      if (id) clearTimeout(id);
    };
  }, []);

  // «Più tardi» non vuol dire «mai più»: torna fra una settimana
  const hide = () => {
    if (!chiesto) localStorage.setItem(KEY, String(Date.now() + RINVIO));
    setShow(false);
    setChiesto(false);
  };

  if (!show) return null;

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    hide();
  };

  const desktop = window.matchMedia("(min-width: 1024px)").matches;

  return (
    <div
      className="fixed bottom-[68px] left-3 right-3 z-[55] flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-2xl lg:bottom-6 lg:left-auto lg:right-6 lg:w-[360px]"
      data-testid="install-prompt"
    >
      <div className="mt-0.5 shrink-0 rounded-lg bg-slate-900 p-2 text-white">
        {desktop ? <MonitorDown className="h-4 w-4" /> : <Download className="h-4 w-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{desktop ? t("install.titlePc") : t("install.title")}</p>
        <p className="mt-0.5 text-xs text-slate-500">
          {prompt ? (desktop ? t("install.subPc") : t("install.sub")) : isIOS() ? t("install.ios") : t("install.manual")}
        </p>
        <div className="mt-3 flex items-center gap-2">
          {prompt ? (
            <button
              onClick={install}
              data-testid="install-accept-btn"
              className="rounded-md bg-slate-900 px-4 py-2.5 text-xs font-bold text-white"
            >
              {desktop ? t("install.ctaPc") : t("install.cta")}
            </button>
          ) : (
            <span className="flex items-center gap-1.5 rounded-md bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
              <Share className="h-3.5 w-3.5" /> {isIOS() ? t("install.iosStep") : t("install.manualStep")}
            </span>
          )}
          <button onClick={hide} data-testid="install-later-btn" className="px-2 py-2.5 text-xs font-semibold text-slate-500">
            {t("install.later")}
          </button>
        </div>
      </div>
      <button onClick={hide} data-testid="install-close-btn" className="shrink-0 rounded-md p-1.5 text-slate-400">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
